import { type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RiskLevels } from "graphql_globals";
import { Badge, type BadgeProps } from "common/core/badge";

export type RiskTextLength = "short" | "medium";

export type Props = {
  aggregateRiskLevel: RiskLevels | null;
  textLength?: RiskTextLength;
};

const NO_RISK_LEVEL = "~no-risk-level~";
type RiskLevelKey = RiskLevels | typeof NO_RISK_LEVEL;

const riskLevelToBadgeProps: Record<RiskLevelKey, Partial<BadgeProps>> = {
  [NO_RISK_LEVEL]: { kind: "infoSubtle" },
  [RiskLevels.HIGH]: { kind: "danger" },
  [RiskLevels.MEDIUM]: { kind: "warning" },
  [RiskLevels.LOW]: { kind: "success" },
};

const shortRiskLevelTexts: Record<RiskLevelKey, ReactNode> = {
  [NO_RISK_LEVEL]: (
    <FormattedMessage id="7e0ca202-3815-4992-81a8-2bfcf7d7142b" defaultMessage="N/A" />
  ),
  [RiskLevels.HIGH]: (
    <FormattedMessage id="7bb75188-9cab-4978-b661-5477caafdd7b" defaultMessage="High" />
  ),
  [RiskLevels.MEDIUM]: (
    <FormattedMessage id="83084c1c-e7dc-4e7c-887a-f47c6f6d700c" defaultMessage="Med" />
  ),
  [RiskLevels.LOW]: (
    <FormattedMessage id="068c7101-d37f-4b86-9b0e-5f6f17e846c5" defaultMessage="Low" />
  ),
};

const mediumRiskLevelTexts: Record<RiskLevelKey, ReactNode> = {
  [NO_RISK_LEVEL]: (
    <FormattedMessage id="7e346e42-fec9-431f-bc16-a6579200e120" defaultMessage="N/A" />
  ),
  [RiskLevels.HIGH]: (
    <FormattedMessage id="7bb75188-9cab-4978-b661-5477caafdd7b" defaultMessage="High" />
  ),
  [RiskLevels.MEDIUM]: (
    <FormattedMessage id="a3b5e26b-3bcd-462d-af28-3654dc9a7899" defaultMessage="Medium" />
  ),
  [RiskLevels.LOW]: (
    <FormattedMessage id="2a3546a9-8f29-4651-8de8-dd430201d018" defaultMessage="Low" />
  ),
};

const textLengthToTexts = {
  short: shortRiskLevelTexts,
  medium: mediumRiskLevelTexts,
};

export default function RiskLevel({ aggregateRiskLevel, textLength = "short" }: Props) {
  const riskKey = aggregateRiskLevel ?? NO_RISK_LEVEL;
  const riskText = textLengthToTexts[textLength][riskKey];
  return <Badge {...riskLevelToBadgeProps[riskKey]}>{riskText}</Badge>;
}
